import React, { useEffect, useState } from "react";

import { useNotify } from "react-admin";

import { Box, CircularProgress, Typography } from "@material-ui/core";
import { ExpansionPanel, ExpansionPanelDetails, ExpansionPanelSummary, List } from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";

import { getQuotes, postDiscountRepairBonus, postPayment, postQuote, updateQuote } from "utils/api";
import { useQuotes } from "utils/hooks/useQuotes";

import NewQuoteForm from "./components/NewQuoteForm";
import QuoteDetails from "./components/QuoteDetails";
import QuoteHeader from "./components/QuoteHeader.component";
import { getLastQuote, getQuoteAmountWithTax } from "./services/QuoteHelper";
import { RepairAssistantService } from "./services/RepairAssistantService";
import { quoteStyles } from "./styles/quoteStyles";

const StyledExpansionPanelDetails = withStyles({
  root: {
    display: "flex",
    padding: "8px 32px 15px",
    textAlign: "right",
  },
})(ExpansionPanelDetails);

const StyledExpansionPanelSummary = withStyles({
  root: {
    display: "flex",
    minHeight: "86px",
    padding: "0 40px 0 40px",
  },
})(ExpansionPanelSummary);

const StyledExpansionPanel = withStyles({
  root: {
    "&:last-child": {
      borderRadius: "8px",
      boxShadow: "none",
    },
  },
})(ExpansionPanel);

const CustomerFileQuoteBilling = ({
  customerFile,
  customerFilesStates,
  employees,
  visits,
  saveCurrentEditionSession,
  resetCurrentEditionSession,
  currentEditionSession,
  classes,
  quoteBillingList,
}) => {
  const notify = useNotify();
  const [isNewQuoteFormOpen, setIsNewQuoteFormOpen] = useState(false);
  const [amountLastAcceptedQuote, setAmountLastAcceptedQuote] = useState(null);
  const [sparePartsOrders, setSparePartsOrders] = useState([]);
  const [hermeticUnitsOrders, setHermeticUnitsOrders] = useState([]);

  const fetchSparePartsAndHermeticUnits = async (lastQuote, visits, customerFile) => {
    const repairAssistantService = new RepairAssistantService();
    const sparePartOrdersFromAssistant = await repairAssistantService.getSparePartOrder(
      customerFile,
      lastQuote.created_at
    );

    // Get all tickets that were created after the last quote
    const newestVisits = visits.filter((visit) => visit.timeslot && visit.timeslot.beginning > lastQuote.created_at);
    const tickets = newestVisits.reduce((tickets, visit) => tickets.concat(visit.tickets), []);
    const products = tickets
      .filter((ticket) => !!ticket.ticket_payload)
      .reduce((products, ticket) => products.concat(ticket.ticket_payload.payload.products), []);

    const productData = products.map((product) => {
      const productId = parseInt(product.product_id);

      const sparePartsOrders = (product.spare_parts_orders || []).map((order) => ({
        ...order,
        productId,
      }));

      const hermeticUnitsOrders = (product.hermetic_unit_order || []).map((order) => ({
        ...order,
        productId,
      }));

      return {
        productId,
        sparePartsOrders,
        hermeticUnitsOrders,
      };
    });

    // Combine all spare parts orders and hermetic units orders
    const sparePartsOrders = productData.flatMap((product) => product.sparePartsOrders);
    const hermeticUnitsOrders = productData.flatMap((product) => product.hermeticUnitsOrders);

    return {
      sparePartsOrders: sparePartsOrders.concat(sparePartOrdersFromAssistant),
      hermeticUnitsOrders,
    };
  };

  const { quotes, lastQuote, isLoading, refetch } = useQuotes(customerFile.id, isNewQuoteFormOpen);
  const [isCreatingQuote, setIsCreatingQuote] = useState(false);

  // Handle edition session setup when quotes load
  useEffect(() => {
    const openDefaultEditionSession = async () => {
      if (quotes.length === 0) return;

      const customerFileState = Object.values(customerFilesStates).find((state) => state.id === customerFile.state);
      const lastQuote = getLastQuote(quotes);
      const lastAcceptedQuote = getLastQuote(quotes.filter((quote) => !!quote.quote_accepted));

      let shouldCreateDraft = false;
      if (
        customerFileState &&
        (customerFileState.name === "CR à traiter" || customerFileState.name === "CR à vérifier")
      ) {
        const { sparePartsOrders, hermeticUnitsOrders } = await fetchSparePartsAndHermeticUnits(
          lastQuote,
          visits,
          customerFile
        );
        if (
          sparePartsOrders.length + hermeticUnitsOrders.length > 0 &&
          lastQuote.finalization_context.context_type !== "visit"
        ) {
          shouldCreateDraft = true;
          setSparePartsOrders(sparePartsOrders);
          setHermeticUnitsOrders(hermeticUnitsOrders);
        }
      }
      const shouldShowPendingDraft = currentEditionSession && currentEditionSession.isEditionSessionPending;

      setIsNewQuoteFormOpen(shouldCreateDraft || shouldShowPendingDraft);
      setAmountLastAcceptedQuote(lastAcceptedQuote && getQuoteAmountWithTax(lastAcceptedQuote.quote_elements));
    };

    openDefaultEditionSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quotes]);

  const handleDisplayNewQuoteForm = (quote) => {
    setIsNewQuoteFormOpen(!isNewQuoteFormOpen);
  };

  const handleQuoteCreation = async (quote, newElements, emailTemplate, announced_delay) => {
    setIsCreatingQuote(true);
    try {
      const mergedElements = newElements ? [...quote.quote_elements, ...newElements] : quote.quote_elements;
      const { data, status } = await postQuote({
        version: lastQuote.version + 1,
        quote_elements: mergedElements,
        customer_file: customerFile.id,
        email_template: emailTemplate,
        announced_delay: announced_delay,
      });

      if (status < 200 || status >= 300) {
        notify(
          `Une erreur est survenue: \n Http status ${status}, \n Http error message ${JSON.stringify(data)}`,
          "error"
        );
      } else {
        setIsNewQuoteFormOpen(false);
        refetch();
        notify("Le devis a été créé", "success");
      }
    } finally {
      setIsCreatingQuote(false);
    }
  };

  const addDiscountRepairBonus = async (quote_uuid, product_id) => {
    try {
      const { data, status } = await postDiscountRepairBonus(quote_uuid, product_id);

      if (status < 200 || status >= 300) {
        notify(
          `Une erreur est survenue: \n Http status ${status}, \n Http error message ${JSON.stringify(data)}`,
          "error"
        );
      } else {
        notify("La remise bonus garanti a bien été appliquée", "success");
        refetch();
      }
    } catch (error) {
      notify(`Une erreur est survenue, le devis n'a pas été modifié: ${error.message}`, "error");
    }
  };

  const handlePaymentCreation = async (
    paymentNature,
    amount,
    paymentType,
    selectedTech,
    selectedDate,
    paymentPartner,
    partnerReference,
    quoteId
  ) => {
    try {
      await postPayment({
        payment_nature: paymentNature,
        amount: amount,
        payment_method: paymentType,
        employee: selectedTech,
        payment_date: selectedDate,
        payment_solution: paymentPartner,
        solution_reference: partnerReference,
        quote_id: quoteId,
        customer_file: customerFile.id,
      });
      notify("Paiement ajouté", "success");
      refetch();
    } catch (error) {
      notify(`Une erreur est survenue, le paiement n'a pas été créé: ${error.message}`, "error");
    }
  };

  const handleValidateQuoteUpdate = async (quote, quoteStatus, sendConfirmationEmail = false) => {
    try {
      const { data, status } = await updateQuote(quote.id, {
        ...quoteStatus,
        send_confirmation_email: sendConfirmationEmail,
      });

      if (status < 200 || status >= 300) {
        notify(
          `Une erreur est survenue: \n Http status ${status}, \n Http error message ${JSON.stringify(data)}`,
          "error"
        );
      } else {
        notify("Le devis a bien été modifié", "success");
        refetch();
      }
    } catch (error) {
      notify(`Une erreur est survenue, le devis n'a pas été modifié: ${error.message}`, "error");
    }
  };

  return (
    <>
      <List>
        {isLoading || isCreatingQuote ? (
          <Box textAlign="center">
            <Typography variant="body1" color="primary">
              {isCreatingQuote ? "Création du devis en cours" : "Chargement des devis en cours"}
            </Typography>
            <CircularProgress color="secondary" />
          </Box>
        ) : (
          quotes.map((quote, index) => (
            <div className={classes.table} key={index}>
              <StyledExpansionPanel defaultExpanded={quote === lastQuote && !isNewQuoteFormOpen}>
                <StyledExpansionPanelSummary>
                  <QuoteHeader record={quote} />
                </StyledExpansionPanelSummary>
                <StyledExpansionPanelDetails>
                  <QuoteDetails
                    record={quote}
                    handlePaymentCreation={handlePaymentCreation}
                    handleDisplayNewQuoteForm={handleDisplayNewQuoteForm}
                    quoteBillingList={quoteBillingList}
                    handleUpdateQuote={handleValidateQuoteUpdate}
                    isNewQuoteFormOpen={isNewQuoteFormOpen}
                    employees={employees}
                    visits={visits}
                    customerFile={customerFile}
                    getQuotes={getQuotes}
                    amountLastAcceptedQuote={amountLastAcceptedQuote}
                    isLast={!(quote === lastQuote && !isNewQuoteFormOpen)}
                    addDiscountRepairBonus={addDiscountRepairBonus}
                  />
                </StyledExpansionPanelDetails>
              </StyledExpansionPanel>
            </div>
          ))
        )}
      </List>
      {isNewQuoteFormOpen && lastQuote && (
        <div id="quote-creation-form">
          <NewQuoteForm
            quote={lastQuote}
            customerFile={customerFile}
            handleQuoteCreation={handleQuoteCreation}
            classes={classes}
            handleDisplayNewQuoteForm={handleDisplayNewQuoteForm}
            employees={employees}
            visits={visits}
            saveCurrentEditionSession={saveCurrentEditionSession}
            resetCurrentEditionSession={resetCurrentEditionSession}
            currentEditionSession={currentEditionSession}
            sparePartsOrders={sparePartsOrders}
            hermeticUnitsOrders={hermeticUnitsOrders}
          />
        </div>
      )}
    </>
  );
};

export default withStyles(quoteStyles)(CustomerFileQuoteBilling);
